import * as React from 'react';

import { ControlLabel } from 'react-bootstrap';
import styled from 'styled-components';
import Select from 'react-select';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Activity } from '../../../types/Activity';
import {
  defaultPaymentProfile,
  paymentProfileOptions,
} from '../../../infrastructure/constants/payments';
import {
  getPaymentProfile,
  getPaymentProfileOptionValue,
  getPaymentProfileOptionLabel,
  getBookingMethod,
} from '../../../infrastructure/utils/payments';
import {
  BookingMethodCapability,
  Capabilities,
  paymentProfileCapabilitySchema,
  bookingMethodCapabilitySchema,
  PaymentProfileCapability,
  StripePaymentProfile,
  notificationCapabilitiesChannelEnum,
  integrationNotificationCapabilityRecipientEnum,
} from '../../../schemas/CapabilitiesSchema';
import { Vendor } from '../../../types/Vendor';
import { StripeAccount } from '../../../types/Stripe';
import {
  getBookingConfirmationRecipient,
  getBookingConfirmationSettings,
} from '../../../infrastructure/utils/order';
import Disable from '../../utils/Disable';

import ValueSelector from './ValueSelector';

const paymentMethodCapabilitiesEnum = z.enum(['connect', 'issuing']);

const FormDataSchema = z
  .object({
    paymentProfileCapability: paymentProfileCapabilitySchema
      .nullable()
      .optional(),
    booking: bookingMethodCapabilitySchema.nullable().optional(),
    paymentMethod: paymentMethodCapabilitiesEnum,
    bookingConfirmationChannel: notificationCapabilitiesChannelEnum.optional(),
    bookingConfirmationRecipient: integrationNotificationCapabilityRecipientEnum.optional(),
  })
  .superRefine((data, ctx) => {
    if (data.paymentMethod === 'connect') {
      const paymentProfile = data.paymentProfileCapability;

      if (!paymentProfile) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Please select Payment Timing when payment method is "connect".',
          path: ['paymentProfileCapability'],
        });
      } else {
        const { paymentAccount } = paymentProfile as StripePaymentProfile;

        if (!paymentAccount) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please select Stripe Account.',
            path: ['paymentProfileCapability', 'paymentAccount'],
          });
        }
      }
    }
  });

type FormData = z.infer<typeof FormDataSchema>;

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  gap: 16px;
  align-items: center;
  justify-content: end;
`;

const StyledForm = styled(Disable)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  .help-block {
    color: red;
  }
`;

const StyledContainer = styled.div`
  width: 75%;
`;

const bookingConfirmationChannelOptions = [
  {
    value: notificationCapabilitiesChannelEnum.Values.integration,
    label: 'Integration',
  },
  {
    value: notificationCapabilitiesChannelEnum.Values.iterable,
    label: 'Iterable',
  },
  { value: notificationCapabilitiesChannelEnum.Values.manual, label: 'Manual' },
];
const bookingConfirmationRecipientOptions = [
  {
    value: integrationNotificationCapabilityRecipientEnum.Values.batch,
    label: 'Batch',
  },
  {
    value: integrationNotificationCapabilityRecipientEnum.Values.user,
    label: 'User',
  },
];

type Props = {
  disabled: boolean;
  onSave: (fields: Partial<Activity>) => void;
} & Pick<Activity, 'meta' | 'metadata'> &
  Pick<Vendor, 'stripeConnectedAccounts'>;

const CapabilitiesSection = ({
  meta,
  metadata,
  onSave,
  stripeConnectedAccounts = [],
  disabled,
}: Props) => {
  const { capabilities } = meta ?? {};

  const isCapabilitesSeted = React.useRef(false);

  const { stripeId } = metadata ?? {};

  const initialPaymentProfile = getPaymentProfile(capabilities);
  const initialBookingMethod = getBookingMethod(capabilities);
  const initialPaymentMethod = initialPaymentProfile ? 'connect' : 'issuing';
  const initialBCNotificationSettings = getBookingConfirmationSettings(
    capabilities
  );

  const { control, watch, setValue, reset, trigger } = useForm<FormData>({
    defaultValues: {
      paymentProfileCapability: initialPaymentProfile,
      booking: initialBookingMethod,
      paymentMethod: initialPaymentMethod,
      bookingConfirmationChannel: initialBCNotificationSettings?.channel,
      bookingConfirmationRecipient: getBookingConfirmationRecipient(
        initialBCNotificationSettings
      ),
    },
    resolver: zodResolver(FormDataSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const [paymentMethod] = watch(['paymentMethod']);
  const isConnect = paymentMethod === 'connect';

  const [bookingConfirmationChannel] = watch(['bookingConfirmationChannel']);

  React.useEffect(() => {
    const init = () => {
      if (isCapabilitesSeted.current) return;
      const initialPaymentProfile = {
        sensitive: true,
        ...defaultPaymentProfile,
        paymentProcessor: 'stripe',
        paymentAccount: stripeId,
      } as StripePaymentProfile;
      setValue(
        'paymentProfileCapability',
        initialPaymentProfile as PaymentProfileCapability
      );
      setValue('paymentMethod', 'connect');
      trigger(['paymentProfileCapability', 'paymentMethod']);

      const metaCapabilities: Capabilities = {
        paymentProfile: initialPaymentProfile,
      };
      if (initialBookingMethod) {
        metaCapabilities.bookingMethod = initialBookingMethod;
      }

      onSave({
        meta: {
          capabilities: metaCapabilities,
        },
      });
      isCapabilitesSeted.current = true;
    };

    if (!!stripeId && !getPaymentProfile(capabilities)) {
      init();
    }
  }, [
    capabilities,
    initialBookingMethod,
    initialPaymentProfile,
    onSave,
    setValue,
    stripeId,
    trigger,
  ]);

  return (
    <StyledForm disabled={disabled} className='data-box'>
      <div className='activity-section-top'>
        <h4 className='header'>Capabilities</h4>
        <FlexRow>
          <button type='submit' className='btn btn-primary'>
            Save
          </button>
          <button
            onClick={() => {
              reset();
              onSave({
                meta: {
                  capabilities: {},
                },
              });
            }}
            className='btn btn-danger'
          >
            Reset
          </button>
        </FlexRow>
      </div>
      <Controller
        control={control}
        name='booking'
        render={({ field }) => (
          <BookingMethodSelector
            bookingMethod={field.value}
            handleSelect={field.onChange}
          />
        )}
      />
      <Controller
        control={control}
        name='paymentMethod'
        render={({ field }) => (
          <PaymentMethodSelector
            paymentMethod={field.value}
            handleSelectPaymentMethod={(paymentMethod: string) => {
              if (paymentMethod === 'issuing') {
                setValue('paymentProfileCapability', null);
              }
              field.onChange(paymentMethod);
            }}
          />
        )}
      />
      {isConnect && (
        <React.Fragment>
          <Controller
            control={control}
            name='paymentProfileCapability'
            render={({ field, fieldState }) => {
              return (
                <PaymentProfileSelector
                  paymentProfile={field.value}
                  handleSelect={field.onChange}
                  error={fieldState.error?.root?.message}
                />
              );
            }}
          />
          <Controller
            control={control}
            name='paymentProfileCapability.paymentAccount'
            render={({ field, fieldState }) => (
              <StripeAccountSelector
                paymentAccount={field.value}
                stripeConnectedAccounts={stripeConnectedAccounts}
                handleSelectPaymentAccount={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />
        </React.Fragment>
      )}

      <Controller
        control={control}
        name='bookingConfirmationChannel'
        render={({ field }) => (
          <ValueSelector
            options={bookingConfirmationChannelOptions}
            value={field.value}
            name='bookingConfirmationChannel'
            label={'Booking Confirmation Notification Channel'}
            placeholder={'Select Notification Channel'}
            handleSelectOption={channel => {
              field.onChange(channel);
              if (
                channel !==
                notificationCapabilitiesChannelEnum.Values.integration
              ) {
                setValue('bookingConfirmationRecipient', undefined);
              } else {
                setValue('bookingConfirmationRecipient', 'batch');
              }

              trigger(['bookingConfirmationRecipient']);
            }}
          />
        )}
      />
      {bookingConfirmationChannel === 'integration' && (
        <Controller
          control={control}
          name='bookingConfirmationRecipient'
          render={({ field }) => (
            <ValueSelector
              options={bookingConfirmationRecipientOptions}
              value={field.value}
              name='bookingConfirmationRecipient'
              label={'Booking Confirmation Notification Recipient'}
              placeholder={'Select Notification Recipient'}
              handleSelectOption={field.onChange}
            />
          )}
        />
      )}
    </StyledForm>
  );
};

const bookingMethodOptions = [
  { value: 'email', label: 'Email' },
  { value: 'default', label: 'Default' },
];

type BookingMethodSelectorProps = {
  bookingMethod: BookingMethodCapability | undefined | null;
  handleSelect: (bookingMethod: BookingMethodCapability | undefined) => void;
};

const BookingMethodSelector = ({
  bookingMethod,
  handleSelect,
}: BookingMethodSelectorProps) => {
  const { kind } = bookingMethod ?? {};

  const selectedBookingMethod = kind
    ? {
        value: kind,
        label: kind.charAt(0).toUpperCase() + kind.slice(1),
      }
    : { value: 'default', label: 'Default' };

  return (
    <StyledContainer>
      <ControlLabel htmlFor='bookingMethod'>Booking Method</ControlLabel>
      <Select
        id='bookingMethod'
        options={bookingMethodOptions}
        defaultValue={selectedBookingMethod}
        placeholder='Select Booking Method'
        onChange={selectedOption => {
          if (!selectedOption?.value) return;
          if (selectedOption.value === 'default') {
            handleSelect(undefined);
            return;
          }
          const bookingMethod = {
            kind: selectedOption.value,
          } as BookingMethodCapability;
          handleSelect(bookingMethod);
        }}
      />
    </StyledContainer>
  );
};

const paymentMethodOptions = [
  { value: 'connect', label: 'Connect' },
  { value: 'issuing', label: 'Issuing' },
];

type PaymentMethodSelectorProps = {
  paymentMethod: string | undefined;
  handleSelectPaymentMethod: (paymentMethod: string) => void;
};

function PaymentMethodSelector({
  paymentMethod,
  handleSelectPaymentMethod,
}: PaymentMethodSelectorProps) {
  const selectedPaymentMethod = paymentMethodOptions.find(
    o => o.value === paymentMethod
  );
  return (
    <StyledContainer>
      <ControlLabel htmlFor='bookingMethod'>Payment Method</ControlLabel>
      <Select
        name='paymentMethod'
        options={paymentMethodOptions}
        defaultValue={selectedPaymentMethod}
        placeholder='Select Payment Method'
        onChange={selectedOption => {
          if (!selectedOption?.value) return;
          handleSelectPaymentMethod(selectedOption.value);
        }}
      />
    </StyledContainer>
  );
}

type PaymentProfileSelectorProps = {
  paymentProfile: PaymentProfileCapability | undefined | null;
  handleSelect: (paymentProfile: PaymentProfileCapability | undefined) => void;
  error: string | undefined;
};

function PaymentProfileSelector({
  paymentProfile,
  handleSelect,
  error,
}: PaymentProfileSelectorProps) {
  const paymentProfileOptionValue = paymentProfile
    ? getPaymentProfileOptionValue(paymentProfile)
    : undefined;

  const selectedPaymentProfile = paymentProfileOptionValue
    ? {
        value: paymentProfileOptionValue,
        label: getPaymentProfileOptionLabel(paymentProfileOptionValue),
      }
    : undefined;

  return (
    <StyledContainer>
      <ControlLabel htmlFor='paymentProfile'>Payment Timing</ControlLabel>
      <Select
        id='paymentProfile'
        options={paymentProfileOptions}
        defaultValue={selectedPaymentProfile}
        placeholder='Select Payment Timing'
        loadingMessage={() => 'Loading...'}
        onChange={selectedOption => {
          if (!selectedOption?.value) return;
          const paymentProfile = {
            sensitive: true,
            paymentProcessor: 'stripe',
            platformProcessingFee: selectedOption.value.platformProcessingFee,
            remittanceSchedule: selectedOption.value.remittanceSchedule,
            remittancePeriod: selectedOption.value.remittancePeriod,
            feeType: selectedOption.value.feeType,
          } as PaymentProfileCapability;
          handleSelect(paymentProfile);
        }}
      />
      {!!error && <span className={'help-block'}>{error}</span>}
    </StyledContainer>
  );
}

type StripeAccountSelectorProps = {
  paymentAccount: string | undefined;
  stripeConnectedAccounts: StripeAccount[];
  handleSelectPaymentAccount: (paymentAccount: string) => void;
  error: string | undefined;
};

function StripeAccountSelector({
  paymentAccount,
  stripeConnectedAccounts,
  handleSelectPaymentAccount,
  error,
}: StripeAccountSelectorProps) {
  const selectedPaymentMethod = paymentAccount
    ? {
        value: paymentAccount,
        label: paymentAccount,
      }
    : undefined;

  return (
    <StyledContainer>
      <ControlLabel htmlFor='bookingMethod'>Stripe Account</ControlLabel>
      <Select
        id='paymentAccount'
        options={stripeConnectedAccounts.map(v => ({
          label: `ID: ${v.id}, Business Name: ${v.businessName}, Email: ${v.email}`,
          value: v.id,
        }))}
        defaultValue={selectedPaymentMethod}
        placeholder='Select Stripe Account'
        onChange={selectedOption => {
          if (!selectedOption?.value) return;
          handleSelectPaymentAccount(selectedOption.value);
        }}
      />
      {!!error && <span className={'help-block'}>{error}</span>}
    </StyledContainer>
  );
}

export default CapabilitiesSection;
